import { Navigate, useLocation } from 'react-router-dom';
import { BackendUnavailablePage } from 'auth/BackendUnavailablePage';
import { useAuth } from 'auth/AuthProvider';
import { firstAccessiblePath, paths, safeReturnTo } from 'core/routing/paths';
import { useI18n } from 'core/i18n/I18nProvider';
import { Skeleton } from 'shared/components/Feedback';

export function ProtectedRoute({ children }) {
  const { status } = useAuth();
  const location = useLocation();
  const { t } = useI18n();

  if (status === 'bootstrapping') {
    return (
      <div className="boot-screen">
        <p>{t('auth.bootstrapping')}</p>
        <Skeleton rows={3} />
      </div>
    );
  }

  if (status === 'temporarily_unavailable') {
    return <BackendUnavailablePage />;
  }

  if (status === 'anonymous') {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`${paths.login}?returnTo=${returnTo}`} replace />;
  }

  if (status === 'forbidden') {
    return <Navigate to={paths.forbidden} replace />;
  }

  return children;
}

export function PermissionRoute({ permission, anyOf, children }) {
  const { hasPermission, status } = useAuth();
  if (status !== 'authenticated') {
    return children;
  }
  const allowed = Array.isArray(anyOf) && anyOf.length > 0
    ? anyOf.some((slug) => hasPermission(slug))
    : hasPermission(permission);
  if (!allowed) {
    return <Navigate to={paths.forbidden} replace />;
  }
  return children;
}

export function GuestRoute({ children }) {
  const { status, admin, permissions } = useAuth();
  const location = useLocation();
  if (status === 'bootstrapping') {
    return <Skeleton rows={3} />;
  }
  if (status === 'temporarily_unavailable') {
    return <BackendUnavailablePage />;
  }
  if (status === 'authenticated') {
    const params = new URLSearchParams(location.search);
    const fallback = firstAccessiblePath(admin || permissions);
    return <Navigate to={safeReturnTo(params.get('returnTo'), fallback)} replace />;
  }
  return children;
}
